import React, { useId, KeyboardEvent, useRef, useEffect } from "react";
import { Tab, TabPanelProps } from "./types";
import { Tooltip } from "../Tooltip/Tooltip";

const TabPanel: React.FC<TabPanelProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  showIcons = true,
  showCount = true,
  alwaysShowLabels = false,
  showTooltips = true,
  containerClassName = "",
  tabListClass = "",
  tabButtonClass = "",
  tabButtonActiveClass = "",
  tabButtonInactiveClass = "",
  tabLabelClass = "",
  tabLabelActiveClass = "",
  tabLabelInactiveClass = "",
  tabIconContainerClassName = "",
  tabIconActiveClass = "",
  tabIconInactiveClass = "",
  tabCountClass = "",
  activeBarClass = "",
  disableAutoFocus = false,
  children,
}) => {
  const tabListId = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Avoid re-focusing the active tab on every rerender when callers recreate `tabs` arrays.
  // We only want to auto-focus when the active tab changes or when the set/order of tab ids changes.
  const tabIdsKey = tabs.map((t) => t.id).join("|");

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, tabs.length);
  }, [tabs.length]);

  useEffect(() => {
    if (disableAutoFocus) return;
    const activeIndex = tabs.findIndex((tab) => tab.id === activeTabId);
    if (activeIndex >= 0 && tabRefs.current[activeIndex]) {
      tabRefs.current[activeIndex]?.focus();
    }
  }, [activeTabId, tabIdsKey, disableAutoFocus]);

  const handleKeyDown = (
    e: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (!["ArrowRight", "ArrowLeft"].includes(e.key)) return;

    e.preventDefault();
    const nextIndex =
      e.key === "ArrowRight"
        ? (index + 1) % tabs.length
        : (index - 1 + tabs.length) % tabs.length;

    onTabChange(tabs[nextIndex].id);
  };

  const renderTabButton = (tab: Tab, index: number) => {
    const isActive = activeTabId === tab.id;
    const count = Number(tab.count ?? 0);

    return (
      <button
        ref={(el) => (tabRefs.current[index] = el)}
        key={tab.id}
        id={`${tabListId}-tab-${tab.id}`}
        role="tab"
        aria-selected={isActive}
        aria-controls={`${tabListId}-panel-${tab.id}`}
        tabIndex={isActive ? 0 : -1}
        onClick={() => onTabChange(tab.id)}
        onKeyDown={(e) => handleKeyDown(e, index)}
        className={`${tabButtonClass} bg-transparent focus:outline-none flex items-center gap-1 justify-between min-w-fit cursor-pointer transition-colors duration-200 relative ${
          isActive
            ? `text-heading-text dark:text-heading-text-dk font-semibold text-md ${tabButtonActiveClass}`
            : `text-heading-text dark:text-heading-text-dk font-semibold text-md ${tabButtonInactiveClass}`
        }`}
      >
        <div className={`flex items-center gap-2 ${tabIconContainerClassName}`}>
          {showIcons && tab.icon && (
            <tab.icon
              className={`w-5 h-5 shrink-0 ${
                isActive ? tabIconActiveClass : tabIconInactiveClass
              }`}
            />
          )}
          {(alwaysShowLabels || isActive) && (
            <span
              className={`${tabLabelClass} transition-opacity duration-200 ${
                alwaysShowLabels || isActive ? "opacity-100" : "opacity-50"
              } ${isActive ? tabLabelActiveClass : tabLabelInactiveClass}`}
            >
              {tab.label}
            </span>
          )}
        </div>

        {showCount && count > 0 && (
          <span
            className={`${tabCountClass} bg-tag-gray dark:bg-tag-gray-dk text-data dark:text-data-dk text-xs px-1 py-[1.5px] rounded-[4px] font-semibold`}
          >
            {tab.count}
          </span>
        )}

        {isActive && (
          <div
            className={`absolute bottom-0 left-0 right-0 h-[3px] bg-verba-blue dark:bg-verba-blue-dk rounded-full ${activeBarClass}`}
          />
        )}
      </button>
    );
  };

  const renderTab = (tab: Tab, index: number) => {
    const button = renderTabButton(tab, index);
    const shouldShowTooltip =
      showTooltips && tab.tooltip && activeTabId !== tab.id;

    return shouldShowTooltip ? (
      <Tooltip key={tab.id} content={tab.tooltip}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  const activeTab = tabs.find((tab) => tab.id === activeTabId);

  return (
    <div className={`flex flex-col ${containerClassName}`}>
      <div
        role="tablist"
        aria-label="Tabs"
        className={`flex items-center gap-4 ${tabListClass}`}
      >
        {tabs.map(renderTab)}
      </div>

      {activeTab && (
        <div>
          <div
            role="tabpanel"
            id={`${tabListId}-panel-${activeTab.id}`}
            aria-labelledby={`${tabListId}-tab-${activeTab.id}`}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default TabPanel;
